import { AlertIcon } from "../../../../shared/infrastructure";
import { useGetUserInfo } from "../../../../shared/infrastructure/services/user.services";
import { useQueryClient } from "@tanstack/react-query";
import { OfferCardSectionT, UseGetOffersFilters } from "../../domain";
import { OrderTypes, SortByList } from "../../../../shared/domain";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type FilterTabs = "queue" | "history";
type DataArrivedT = "new" | "updated" | "deleted" | "status-changed";

type SetFilterT = <
  N extends keyof UseGetOffersFilters,
  V extends UseGetOffersFilters[N]
>(
  name: N,
  value: V
) => void;

type OffersContextT = {
  newDataArrived: (type: DataArrivedT, offerId: string) => void;
  queueFilters: UseGetOffersFilters;
  historyFilters: UseGetOffersFilters;
  setQueueFilter: SetFilterT;
  setResetFilters: React.Dispatch<React.SetStateAction<boolean>>;
  resetFilters: boolean;
  setHistoryFilter: SetFilterT;
  setTypeFilters: (type: FilterTabs, filters: UseGetOffersFilters) => void;
  clearFilters: (type: FilterTabs) => void;
  newOffers: Record<string, boolean>;
  updatedOffers: Record<string, boolean>;
  deletedOffers: Record<string, boolean>;
  statusChangedOffers: Record<string, boolean>;
  updateNewOffers: () => void;
};

const OffersContext = createContext<OffersContextT | null>(null);

if (process.env.NODE_ENV === "development") {
  OffersContext.displayName = "OffersContext";
}

type OffersProviderProps = {
  children?: React.ReactNode;
};

const OffersProvider = ({
  children,
}: OffersProviderProps): React.ReactElement => {
  const { data: user } = useGetUserInfo();
  const [resetFilters, setResetFilters] = useState(false);
  const [offers, setOffers] = useState<{
    newOffers: Record<string, boolean>;
    updatedOffers: Record<string, boolean>;
    statusChangedOffers: Record<string, boolean>;
    deletedOffers: Record<string, boolean>;
  }>({
    newOffers: {},
    updatedOffers: {},
    statusChangedOffers: {},
    deletedOffers: {},
  });
  const [filters, setFilters] = useState<{
    queue: UseGetOffersFilters;
    history: UseGetOffersFilters;
  }>({
    queue: {
      sort_by: SortByList[0].value,
      order_by: OrderTypes.DESC,
    },
    history: {
      sort_by: SortByList[0].value,
      order_by: OrderTypes.DESC,
    },
  });

  const queueFilters = useMemo(() => filters.queue, [filters.queue]);

  const historyFilters = useMemo(() => filters.history, [filters.history]);

  const newDataArrived = useCallback((type: DataArrivedT, offerId: string) => {
    setOffers((prev) => ({
      ...prev,
      newOffers: {
        ...prev["newOffers"],
        ...(type === "new" ? { [offerId]: false } : {}),
      },
      updatedOffers: {
        ...prev["updatedOffers"],
        ...(type === "updated" ? { [offerId]: false } : {}),
      },
      deletedOffers: {
        ...prev["deletedOffers"],
        ...(type === "deleted" ? { [offerId]: false } : {}),
      },
      statusChangedOffers: {
        ...prev["statusChangedOffers"],
        ...(type === "status-changed" ? { [offerId]: false } : {}),
      },
    }));
  }, []);

  const updateNewOffers = useCallback(() => {
    const reduceCallback = (
      prev: Record<string, boolean>,
      [key, value]: [string, boolean]
    ): Record<string, boolean> =>
      value
        ? prev
        : {
            ...prev,
            [key]: true,
          };

    setOffers((prev) => ({
      newOffers: Object.entries(prev.newOffers).reduce(reduceCallback, {}),
      updatedOffers: Object.entries(prev.updatedOffers).reduce(
        reduceCallback,
        {}
      ),
      deletedOffers: Object.entries(prev.deletedOffers).reduce(
        reduceCallback,
        {}
      ),
      statusChangedOffers: Object.entries(prev.statusChangedOffers).reduce(
        reduceCallback,
        {}
      ),
    }));
  }, []);

  const setTypeFilters = useCallback(
    (type: FilterTabs, filters: UseGetOffersFilters) => {
      setFilters((prev) => ({
        ...prev,
        [type]: filters,
      }));
    },
    []
  );

  const clearFilters = useCallback<OffersContextT["clearFilters"]>((type) => {
    setFilters((prev) => ({
      ...prev,
      [type]: {},
    }));
  }, []);

  const setQueueFilter = useCallback<SetFilterT>((name, value) => {
    setFilters((prev) => ({
      ...prev,
      queue: {
        ...prev.queue,
        [name]: value,
      },
    }));
  }, []);

  const setHistoryFilter = useCallback<SetFilterT>((name, value) => {
    setFilters((prev) => ({
      ...prev,
      history: {
        ...prev.history,
        [name]: value,
      },
    }));
  }, []);

  useEffect(() => {
    if (user?.default_queue_list_order_by) {
      setQueueFilter("order_by", user?.default_queue_list_order_by);
    }

    if (user?.default_queue_list_sort_by) {
      setQueueFilter("sort_by", user?.default_queue_list_sort_by);
    }
  }, [setQueueFilter, user]);

  return (
    <OffersContext.Provider
      value={{
        newDataArrived,
        clearFilters,
        setTypeFilters,
        setQueueFilter,
        setHistoryFilter,
        setResetFilters,
        resetFilters,
        queueFilters,
        historyFilters,
        newOffers: offers.newOffers,
        updatedOffers: offers.updatedOffers,
        deletedOffers: offers.deletedOffers,
        statusChangedOffers: offers.statusChangedOffers,
        updateNewOffers: updateNewOffers,
      }}
    >
      {children}
    </OffersContext.Provider>
  );
};

const useOffers = (): OffersContextT => {
  const context = useContext(OffersContext);

  if (context === null) {
    throw new Error("useOffers must be used within a OffersProvider");
  }

  return context;
};

const useOfferNotification = (tab: OfferCardSectionT): React.ReactNode => {
  const context = useContext(OffersContext);
  const queryClient = useQueryClient();

  if (context === null) {
    throw new Error(
      "useOfferNotification must be used within a OffersProvider"
    );
  }

  const {
    updateNewOffers,
    statusChangedOffers,
    newOffers,
    setResetFilters,
    updatedOffers,
    historyFilters,
    queueFilters,
  } = context;

  const refreshLists = useCallback(async () => {
    updateNewOffers();
    await queryClient.invalidateQueries(["offers"] as const);
  }, [updateNewOffers, queryClient]);

  const getDataLabel = useCallback(
    (count: number): React.ReactElement => {
      const handleRefreshLists = async (): Promise<void> => {
        await refreshLists();

        if (
          Object.keys(historyFilters).length ||
          Object.keys(queueFilters).length
        ) {
          setResetFilters(true);
        }
      };

      return (
        <button
          className="mx-auto grid w-[648px] grid-flow-col items-center justify-start gap-[10px] rounded-[5px] bg-[#2196F3] px-[30px] py-[8px] text-[14px] font-[500] leading-[1em] text-[#FFFFFF]"
          onClick={handleRefreshLists}
        >
          <AlertIcon width={20} height={20} /> Show new approvals ({count})
        </button>
      );
    },
    [refreshLists, setResetFilters, historyFilters, queueFilters]
  );

  const countPendingNotifications = useCallback(
    (object: Record<string, boolean>) =>
      Object.values(object).reduce(
        (prev, value) => (value ? prev : prev + 1),
        0
      ),
    []
  );

  const newPendingCount = useMemo(
    () => countPendingNotifications(newOffers),
    [newOffers, countPendingNotifications]
  );
  const updatedPendingCount = useMemo(
    () => countPendingNotifications(updatedOffers),
    [updatedOffers, countPendingNotifications]
  );
  const statusChangedPendingCount = useMemo(
    () => countPendingNotifications(statusChangedOffers),
    [statusChangedOffers, countPendingNotifications]
  );

  if ((tab === "queue" && newPendingCount > 0) || updatedPendingCount > 0) {
    return getDataLabel(newPendingCount + updatedPendingCount);
  }

  if (tab === "history" && statusChangedPendingCount > 0) {
    return getDataLabel(statusChangedPendingCount);
  }

  return <React.Fragment />;
};

export type { SetFilterT };
export { OffersProvider, useOffers, useOfferNotification };
